import { Header } from "@/components/Header";
import { ChevronRight } from "lucide-react";
import { useSubjects } from "@/contexts/SubjectsContext";

export function SubjectSelectionPage() {
  const { subjects, loading } = useSubjects();

  return (
    <div className="min-h-screen bg-[#f8f9fc] flex flex-col">
      <Header />

      {/* Hero banner */}
      <div className="bg-gradient-to-r from-[#070f24] via-[#0d2248] to-[#1a3a6b]">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <p className="text-[#f5a623] text-sm font-semibold tracking-wide uppercase mb-2">Practice by Subject</p>
          <h1 className="text-2xl md:text-3xl font-extrabold text-white mb-1">Question Banks</h1>
          <p className="text-white/60 text-sm">Choose a subject to drill topics, track accuracy, and review every session.</p>
        </div>
      </div>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#5a6a7e]">Loading subjects…</div>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {subjects.map((subject) => {
            const totalQ = subject.modules.reduce(
              (acc, m) => acc + m.topics.reduce((a, t) => a + (t.questionCount || 0), 0), 0
            );
            const isComingSoon = subject.comingSoon;
            const cardClass = isComingSoon
              ? "bg-white border border-[#dde3ed] rounded-2xl overflow-hidden shadow-sm opacity-70 cursor-default"
              : "group bg-white border border-[#dde3ed] rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:border-[#1a3a6b]/30 transition-all duration-200";

            const inner = (
              <>
                <div className={`h-2 bg-gradient-to-r ${subject.gradient}`} />
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h2 className={`font-bold text-lg leading-tight ${isComingSoon ? "text-[#5a6a7e]" : "text-[#1a2435] group-hover:text-[#1a3a6b] transition-colors"}`}>
                      {subject.title}
                    </h2>
                    {isComingSoon ? (
                      <span className="text-xs font-bold text-amber-700 bg-amber-100 border border-amber-200 px-2.5 py-1 rounded-full whitespace-nowrap ml-2 flex-shrink-0">
                        Coming Soon
                      </span>
                    ) : totalQ > 0 ? (
                      <span className="text-xs font-semibold text-[#1a3a6b] bg-[#1a3a6b]/10 px-2.5 py-1 rounded-full whitespace-nowrap ml-2 flex-shrink-0">
                        {totalQ.toLocaleString()}+ Qs
                      </span>
                    ) : null}
                  </div>

                  <p className="text-sm text-[#5a6a7e] mb-5 line-clamp-2 leading-relaxed">
                    {isComingSoon ? "This question bank is coming soon. Tutoring is available now." : subject.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-[#5a6a7e] bg-[#f8f9fc] border border-[#dde3ed] px-2.5 py-1 rounded-full">
                      {subject.modules.length} module{subject.modules.length !== 1 ? "s" : ""}
                    </span>
                    {isComingSoon ? (
                      <span className="text-xs font-semibold text-[#5a6a7e]">Notify me</span>
                    ) : (
                      <span className="flex items-center gap-1 text-sm font-bold text-[#f5a623] group-hover:translate-x-0.5 transition-transform">
                        Start <ChevronRight className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                </div>
              </>
            );

            return isComingSoon ? (
              <div key={subject.id} className={cardClass}>{inner}</div>
            ) : (
              <a key={subject.id} href={`/subjects/${subject.id}`} className={cardClass}>{inner}</a>
            );
          })}
        </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#dde3ed] bg-white mt-10 py-6">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5a6a7e]">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Scholar Sphere" className="h-6" />
            <span>© {new Date().getFullYear()} Scholar Sphere</span>
          </div>
          <div className="flex gap-5">
            <a href="/dashboard" className="hover:text-[#1a3a6b]">Dashboard</a>
            <a href="/privacy" className="hover:text-[#1a3a6b]">Privacy</a>
            <a href="/terms" className="hover:text-[#1a3a6b]">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
